import React from "react";
import { Heart, ChevronLeft, ChevronRight, Home } from "lucide-react";
import { InteractPrompt } from "@/components/game/InteractPrompt";
import { getMohallaSprite, getInviteLine } from "@/lib/mohallaSprites";

const Prompt = InteractPrompt;

export const MohallaZone = ({ zone, active, unlocked, playable, onClick }) => {
  if (zone.kind === "home") {
    return (
      <div className="absolute z-20" style={{ left: `${zone.x}%`, top: `${zone.y}%`, transform: "translate(-50%, -50%)" }}>
        {active && <Prompt />}
        <button
          data-testid="mohalla-home-exit"
          onClick={onClick}
          className={`flex flex-col items-center gap-1 rounded-md border-[3px] border-kirana-brown bg-kirana-red p-3 text-kirana-cream transition-transform hover:scale-105 ${active ? "zone-glow" : "hard-shadow-sm"}`}
        >
          <Home className="h-6 w-6" strokeWidth={2.5} />
          <span className="font-signboard max-w-[6rem] text-center text-[10px] font-bold leading-tight">Nostalgic World</span>
        </button>
      </div>
    );
  }
  if (zone.kind === "path") {
    const Arrow = zone.dir === "next" ? ChevronRight : ChevronLeft;
    return (
      <div className="absolute z-20" style={{ left: `${zone.x}%`, top: `${zone.y}%`, transform: "translate(-50%, -50%)" }}>
        {active && <Prompt />}
        <button
          data-testid={`path-${zone.dir}`}
          onClick={onClick}
          className={`flex flex-col items-center gap-1 rounded-md border-[3px] border-kirana-brown bg-kirana-brown/80 p-3 text-kirana-cream transition-transform hover:scale-105 ${active ? "zone-glow" : "hard-shadow-sm"}`}
        >
          <Arrow className="h-6 w-6" strokeWidth={2.5} />
          <span className="font-signboard max-w-[6rem] text-center text-[10px] font-bold leading-tight">{zone.label}</span>
        </button>
      </div>
    );
  }

  const sprite = getMohallaSprite(zone.name);
  const LINE = getInviteLine(zone.name);

  return (
    <div className="absolute z-20" style={{ left: `${zone.x}%`, top: `${zone.y}%`, transform: "translate(-50%, -50%)" }}>
      {active && <Prompt />}
      <button
        data-testid={`game-${zone.id}`}
        onClick={onClick}
        className={`group relative flex w-[118px] flex-col items-center transition-transform duration-150 hover:scale-[1.04] ${active ? "scale-[1.04] z-30" : ""}`}
      >
        {/* contextual invite */}
        <div className={`pointer-events-none absolute -top-6 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-md border-2 border-kirana-brown bg-kirana-cream px-2 py-[2px] font-signboard text-[10px] font-bold text-kirana-brown shadow-[1px_1px_0_rgba(0,0,0,0.3)] transition-opacity ${active ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
          {LINE} <span className="text-kirana-red">▶ PLAY</span>
          <span className="absolute -bottom-[5px] left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 border-b-2 border-r-2 border-kirana-brown bg-kirana-cream" />
        </div>

        {/* uniform hand-painted wooden signboard */}
        <div className={`relative w-[118px] rounded-md border-[3px] border-[#3a2617] bg-gradient-to-b from-[#a06b3f] to-[#7a5230] p-1.5 shadow-[0_3px_0_rgba(0,0,0,0.35)] ${active ? "sprite-active ring-2 ring-kirana-gold" : ""}`}>
          {/* corner nails */}
          <span className="absolute left-1 top-1 h-1 w-1 rounded-full bg-black/45" />
          <span className="absolute right-1 top-1 h-1 w-1 rounded-full bg-black/45" />
          {/* framed picture window — sprite sits inside at equal size */}
          <div className="flex h-[72px] w-full items-end justify-center overflow-hidden rounded-sm border-2 border-[#2f1e12] bg-gradient-to-b from-[#f6ead0] to-[#dcc79c]">
            {sprite ? (
              <img
                src={sprite}
                alt={zone.name}
                draggable={false}
                style={{ animationDelay: `${((zone.x + zone.y) % 20) / 10}s` }}
                className="sprite-bob h-[68px] w-auto max-w-full select-none object-contain drop-shadow-[0_2px_2px_rgba(0,0,0,0.35)]"
              />
            ) : (
              <span className="mb-3 flex h-9 w-9 items-center justify-center rounded-full bg-kirana-green/80 text-kirana-cream">🎮</span>
            )}
          </div>
          {/* engraved name plate */}
          <div className="mt-1 rounded-sm border border-black/30 bg-[#33200f] px-1 py-0.5">
            <span className="font-signboard block truncate text-center text-[11px] font-bold leading-tight text-kirana-cream drop-shadow-[0_1px_0_rgba(0,0,0,0.6)]">{zone.name}</span>
          </div>
        </div>

        {/* wooden post + ground shadow so the sign is planted in the world */}
        <span className="h-2.5 w-2.5 bg-gradient-to-b from-[#6e4a29] to-[#4a3018]" />
        <span className="-mt-[1px] h-2 w-14 rounded-[50%] bg-black/30 blur-[2px]" />

        {unlocked && (
          <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border-2 border-kirana-brown bg-kirana-cream">
            <Heart className="h-3 w-3 fill-kirana-red text-kirana-red" />
          </span>
        )}
      </button>
    </div>
  );
};

